import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiCheckCircle, FiXCircle, FiEdit2, FiAlertTriangle } from 'react-icons/fi';
import { fetchProposals, reviewProposal } from '../../utils/sentimentApi';

const STATUS_COLORS = {
  pending:  'sm-badge-pending',
  approved: 'sm-badge-approved',
  rejected: 'sm-badge-rejected',
  modified: 'sm-badge-monitoring',
};

const TYPE_LABELS = {
  auto_ticket:     '🎫 Auto Ticket',
  churn_flag:      '⚡ Churn Flag',
  discount_offer:  '💰 Discount Offer',
  report:          '📊 Report',
};

export default function SentimentAutomations({ showToast }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');

  const [reviewModal, setReviewModal]   = useState(null);
  const [modifiedAction, setModifiedAction] = useState('');
  const [actionLoading, setActionLoading]   = useState(false);

  const userEmail = localStorage.getItem('user') || 'admin@savi.com';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchProposals(filterStatus);
      setProposals(Array.isArray(data) ? data : (data.items || []));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleReview = async (action) => {
    if (!reviewModal) return;
    setActionLoading(true);
    try {
      await reviewProposal(reviewModal.proposal_id, {
        action,
        reviewed_by: userEmail,
        modified_action: action === 'modified' ? modifiedAction.trim() || null : null,
      });
      setProposals(prev => prev.map(p =>
        p.proposal_id === reviewModal.proposal_id ? { ...p, status: action } : p
      ));
      setReviewModal(null);
      setModifiedAction('');
      showToast?.(`Proposal ${action}`, 'success');
    } catch (e) {
      setError(e.message);
      showToast?.(e.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const fmtCurrency = (val) => {
    if (val == null) return null;
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000)   return `₹${(val / 100000).toFixed(1)}L`;
    return `₹${val.toLocaleString()}`;
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

  return (
    <div>
      <div className="sm-section-header">
        <h2 className="sm-section-title">AI Automation Proposals ({proposals.length})</h2>
        <div className="sm-filters">
          <select className="sm-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="modified">Modified</option>
            <option value="">All</option>
          </select>
          <button className="sm-btn sm-btn-ghost" onClick={load}><FiRefreshCw size={14} /></button>
        </div>
      </div>

      {error && (
        <div className="sm-alert-err"><FiAlertTriangle /> {error}</div>
      )}

      {loading ? (
        <div className="sm-loading"><div className="sm-spinner sm-spinner-lg" /><span>Loading proposals…</span></div>
      ) : proposals.length === 0 ? (
        <div className="sm-empty"><div className="sm-empty-icon">🤖</div><span>No proposals found.</span></div>
      ) : (
        <div className="sm-proposal-grid">
          {proposals.map((p, i) => (
            <div className="sm-proposal-card" key={p.proposal_id} style={{ '--delay': `${i * 50}ms` }}>
              <div className="sm-proposal-header">
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sm-primary)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>
                    {TYPE_LABELS[p.proposal_type] || p.proposal_type}
                  </div>
                  <div className="sm-proposal-title">{p.proposed_action}</div>
                  <div className="sm-proposal-customer">{p.customer_id}</div>
                </div>
                <span className={`sm-badge ${STATUS_COLORS[p.status] || ''}`}>{p.status}</span>
              </div>

              <div className="sm-proposal-reason">{p.reason}</div>

              {p.details && (
                <div className="sm-proposal-detail">
                  <span>{typeof p.details === 'string' ? p.details : JSON.stringify(p.details)}</span>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--sm-muted)' }}>
                  {p.account_value != null && (
                    <span>Account: <strong style={{ color: 'var(--sm-ink)' }}>{fmtCurrency(p.account_value)}</strong></span>
                  )}
                </div>
                <span style={{ fontSize: 11, color: 'var(--sm-muted)' }}>{fmtDate(p.created_at)}</span>
              </div>

              {p.account_value >= 1000000 && p.status === 'pending' && (
                <div style={{ fontSize: 11, background: 'var(--sm-amber-lt)', color: 'var(--sm-amber)', borderRadius: 6, padding: '4px 8px', fontWeight: 600 }}>
                  ⚠️ High-value account — manual approval required
                </div>
              )}

              {p.status === 'pending' && (
                <div className="sm-proposal-actions">
                  <button
                    className="sm-btn sm-btn-primary"
                    style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}
                    onClick={() => { setReviewModal(p); setModifiedAction(''); }}
                  >
                    <FiCheckCircle size={13} /> Review
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Review modal */}
      {reviewModal && (
        <div className="sm-modal-overlay" onClick={() => setReviewModal(null)}>
          <div className="sm-modal" onClick={e => e.stopPropagation()}>
            <h3 className="sm-modal-title">Review Proposal</h3>
            <p style={{ fontSize: 13, color: 'var(--sm-muted)', margin: 0 }}>
              <strong>{reviewModal.proposed_action}</strong> for {reviewModal.customer_id}
            </p>
            <div className="sm-proposal-detail" style={{ margin: '4px 0' }}>
              <span>{reviewModal.reason}</span>
              {reviewModal.account_value != null && (
                <span>Account value: <strong>{fmtCurrency(reviewModal.account_value)}</strong></span>
              )}
            </div>
            <div className="sm-field">
              <label>Modified Action (optional — fill if modifying)</label>
              <input
                type="text"
                value={modifiedAction}
                onChange={e => setModifiedAction(e.target.value)}
                placeholder="Describe the modified action…"
              />
            </div>
            <div className="sm-modal-footer" style={{ justifyContent: 'space-between' }}>
              <button
                className="sm-btn sm-btn-danger"
                onClick={() => handleReview('rejected')}
                disabled={actionLoading}
              >
                <FiXCircle size={13} /> Reject
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                {modifiedAction.trim() && (
                  <button
                    className="sm-btn sm-btn-outline"
                    onClick={() => handleReview('modified')}
                    disabled={actionLoading}
                  >
                    <FiEdit2 size={13} /> Modify
                  </button>
                )}
                <button
                  className="sm-btn sm-btn-primary"
                  onClick={() => handleReview('approved')}
                  disabled={actionLoading}
                >
                  <FiCheckCircle size={13} /> {actionLoading ? 'Saving…' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
